/**
 * SoundManager - Handles UI sound effects
 *
 * Currently uses Web Audio API for simple procedural sounds.
 * To use real audio files: Replace generateTone() calls with audio.play()
 */
export class SoundManager {
    constructor() {
        this.audioContext = null;
        this.isMuted = true; // Default muted, syncs with music toggle
        this.volume = 0.15; // Low volume for subtlety

        // Audio element references for pre-loaded sounds
        // (for future implementation with real audio files)
        this.sounds = {};

        this.initAudioContext();
    }

    initAudioContext() {
        // Create Web Audio API context for procedural sounds
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
            console.warn('Web Audio API not supported', e);
        }
    }

    /**
     * Resume audio context (required for mobile browsers)
     * Call this on first user interaction to unlock audio
     */
    async resumeAudioContext() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            try {
                await this.audioContext.resume();
                console.log('Audio context resumed');
            } catch (e) {
                console.warn('Failed to resume audio context:', e);
            }
        }
    }

    /**
     * Set mute state (syncs with existing music toggle)
     */
    setMuted(muted) {
        this.isMuted = muted;
    }

    /**
     * Generate a simple tone using Web Audio API
     * Replace this with audio file playback for production
     */
    generateTone(frequency, duration, type = 'sine') {
        if (this.isMuted || !this.audioContext) return;

        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = type;

        // Envelope: fade in and out
        const now = this.audioContext.currentTime;
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(this.volume, now + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration);

        oscillator.start(now);
        oscillator.stop(now + duration);
    }

    /**
     * Play donut click sound - soft, playful pluck
     * TODO: Replace with audio file: /sounds/donut-click.mp3
     */
    playDonutClick() {
        // Playful ascending pluck sound
        this.generateTone(523, 0.08, 'sine'); // C5
        setTimeout(() => this.generateTone(659, 0.08, 'sine'), 50); // E5
    }

    /**
     * Play portal whoosh + chime
     * TODO: Replace with audio files: /sounds/portal-whoosh.mp3, /sounds/portal-chime.mp3
     */
    playPortalSound() {
        if (this.isMuted || !this.audioContext) return;

        // Whoosh (low frequency sweep)
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.type = 'sawtooth';

        const now = this.audioContext.currentTime;
        oscillator.frequency.setValueAtTime(80, now);
        oscillator.frequency.exponentialRampToValueAtTime(400, now + 0.5);

        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(this.volume * 0.8, now + 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

        oscillator.start(now);
        oscillator.stop(now + 0.5);

        // Chime at the end
        setTimeout(() => {
            this.generateTone(880, 0.3, 'sine'); // A5
            setTimeout(() => this.generateTone(1047, 0.3, 'sine'), 100); // C6
        }, 400);
    }

    /**
     * Play project click sound - subtle tick
     * TODO: Replace with audio file: /sounds/project-click.mp3
     */
    playProjectClick() {
        this.generateTone(698, 0.05, 'square'); // F5, short tick
    }

    /**
     * Play hover sound - very quiet interaction cue
     * TODO: Replace with audio file: /sounds/hover.mp3
     */
    playHoverSound() {
        if (this.isMuted || !this.audioContext) return;

        // Very subtle, high-pitched tick
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = 1760; // A6
        oscillator.type = 'sine';

        const now = this.audioContext.currentTime;
        gainNode.gain.setValueAtTime(this.volume * 0.3, now); // Extra quiet
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.03);

        oscillator.start(now);
        oscillator.stop(now + 0.03);
    }

    /**
     * Play character wave sound - friendly greeting
     * TODO: Replace with audio file: /sounds/character-wave.mp3
     */
    playCharacterWave() {
        // Happy ascending notes
        this.generateTone(523, 0.1, 'sine'); // C5
        setTimeout(() => this.generateTone(659, 0.1, 'sine'), 80); // E5
        setTimeout(() => this.generateTone(784, 0.15, 'sine'), 160); // G5
    }

    /**
     * Load audio file (for future implementation)
     * Example usage:
     * await soundManager.loadSound('donutClick', '/sounds/donut-click.mp3');
     */
    async loadSound(name, url) {
        try {
            const audio = new Audio(url);
            audio.volume = this.volume;
            await audio.load();
            this.sounds[name] = audio;
            console.log(`Sound loaded: ${name}`);
        } catch (error) {
            console.warn(`Failed to load sound: ${name}`, error);
        }
    }

    /**
     * Play loaded audio file (for future implementation)
     */
    playLoadedSound(name) {
        if (this.isMuted || !this.sounds[name]) return;

        const audio = this.sounds[name].cloneNode();
        audio.volume = this.volume;
        audio.play().catch(e => console.warn('Failed to play sound:', e));
    }

    /**
     * Clean up resources
     */
    dispose() {
        if (this.audioContext) {
            this.audioContext.close();
        }
    }
}
